// src/pages/SearchPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getItems, getCategories } from '../services/api';
import { thumbUrl } from '../components/PhotoUpload';

// Order the tool/asset category buttons by how often NGC actually reaches for
// them. This is interiors and renovation work, not heavy construction, so
// layout, hand tools and power distribution matter more than air tools.
//
// The first five are David's call. The rest are ordered on that same reasoning:
// hand tools and layout are on every interior job, small equipment and cords
// follow, safety gear is usually issued rather than searched for, and air tools
// are largely displaced by cordless.
//
// Codes missing from this list still appear, appended alphabetically, so adding
// a category in the database can never make it silently vanish from the UI.
const ASSET_CATEGORY_ORDER = [
  'PWR',    // Power Tools
  'BATT',   // Batteries & Chargers
  'ENV',    // Temp Environmental Control
  'TEMP',   // Temp Structures
  'LADR',   // Ladders & Lifts
  'HAND',   // Hand Tools
  'MEAS',   // Measuring & Layout
  'EQUIP',  // Small Equipment
  'CORD',   // Cords & Hoses
  'SAFE',   // Safety Equipment
  'AIR',    // Air Tools
];

// Short display labels so every tile fits on one line. The database keeps the
// full category names — this only shortens what the button shows. Codes with no
// entry fall back to the real name and clip with an ellipsis rather than wrap.
const SHORT_LABELS = {
  BATT:  'Batteries',
  ENV:   'Temp Climate',
  TEMP:  'Temp Structures',
  LADR:  'Ladders & Lifts',
  MEAS:  'Layout',
  EQUIP: 'Small Equip',
  SAFE:  'Safety',
  CORD:  'Cords & Hoses',
};

const shortLabel = (c) => SHORT_LABELS[c.code] || c.name;

// One coordinated hue per row, lighter on the left and darker on the right.
// All of these carry white text at accessible contrast.
const ROW_COLORS = [
  ['#F0913F', '#D9700F'],  // orange
  ['#41A9A0', '#1F7B74'],  // teal
  ['#5090D6', '#2A63A8'],  // blue
  ['#8B7BD8', '#5E4CA8'],  // violet
  ['#5FA55F', '#3A7A3A'],  // green
  ['#7E8898', '#515B69'],  // slate
];

export default function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [total, setTotal] = useState(0);

  // Load all items on mount
  useEffect(() => {
    loadItems();
  }, []);

  // Picking a type reveals that type's categories as tap targets, so nobody has
  // to scroll a long dropdown on a phone. Changing type clears the category.
  useEffect(() => {
    setCategoryFilter('');
    if (!typeFilter) {
      setCategories([]);
      return;
    }
    getCategories({ item_type: typeFilter })
      .then(r => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, [typeFilter]);

  // Re-search when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadItems();
    }, 300); // Debounce typing
    return () => clearTimeout(timer);
  }, [query, typeFilter, statusFilter, categoryFilter]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await getItems({
        search:   query.trim()   || undefined,
        type:     typeFilter     || undefined,
        status:   statusFilter    || undefined,
        category: categoryFilter || undefined,
        limit:    100
      });
      const items = res.data.items || [];
      setResults(items);
      setTotal(items.length);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => (
    <span className={`badge badge-${status}`} style={{ fontSize: '0.65rem' }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );

  const getCategoryIcon = (code) => {
    const icons = {
      PWR: '🔌', HAND: '🔨', AIR: '💨', ENV: '🌡️',
      BATT: '🔋', SAFE: '🦺', TEMP: '🚧', LADR: '🪜',
      MEAS: '📐', CORD: '🔌', EQUIP: '⚙️',
      DWLL: '🧱', FRAM: '🏗️', FAST: '🔩', FINS: '🪣',
      ELEC: '⚡', PLMB: '🔧', PANT: '🎨', INSL: '📦',
      CEIL: '🏢', CHEM: '🧴', MISC: '📦'
    };
    return icons[code] || '📦';
  };

  // Curated order for tools/assets; plain alphabetical for bulk until there is a
  // reason to curate that too. Anything not in the curated list lands at the end.
  const sortedCategories = [...categories].sort((a, b) => {
    if (typeFilter !== 'asset') return (a.name || '').localeCompare(b.name || '');
    const ia = ASSET_CATEGORY_ORDER.indexOf(a.code);
    const ib = ASSET_CATEGORY_ORDER.indexOf(b.code);
    if (ia === -1 && ib === -1) return (a.name || '').localeCompare(b.name || '');
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return (
    <div className="screen">
      <div className="screen-header" style={{ justifyContent: 'space-between' }}>
        <h2>Inventory</h2>
        {!loading && (
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {total} item{total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="screen-content">

        {/* Search input */}
        <input
          className="input"
          placeholder="Search by name, asset tag, serial #..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ marginBottom: 10 }}
          autoComplete="off"
        />

        {/* Type filter — stacked full-width buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
          {[
            { value: '',      label: 'All Types' },
            { value: 'asset', label: 'Tools / Assets' },
            { value: 'bulk',  label: 'Bulk / Consumables' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setTypeFilter(opt.value)}
              style={{
                width: '100%',
                padding: '13px 16px',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                border: typeFilter === opt.value ? 'none' : '1px solid var(--border)',
                background: typeFilter === opt.value ? 'var(--ngc-orange)' : 'var(--bg-elevated)',
                color: typeFilter === opt.value ? 'white' : 'var(--text-secondary)',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div style={{ marginBottom: 16 }}>
          <select
            className="input"
            style={{ padding: '10px 12px', width: '100%' }}
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="available">Available</option>
            <option value="checked_out">Checked Out</option>
            <option value="in_transit">In Transit</option>
            <option value="on_site">On Site</option>
            <option value="in_repair">In Repair</option>
            <option value="lost">Lost</option>
          </select>
        </div>

        {/* Category buttons — appear once a type is chosen. Two columns of large
            tap targets beats scrolling a dropdown one-handed on a phone. Counts
            are shown so an empty category is obvious before you tap it. */}
        {typeFilter && sortedCategories.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <button
              onClick={() => setCategoryFilter('')}
              style={{
                width: '100%', marginBottom: 8, padding: '10px 12px',
                borderRadius: 10, fontSize: '0.85rem', fontWeight: 700,
                cursor: 'pointer', textAlign: 'center',
                background: categoryFilter === '' ? 'var(--ngc-orange)' : 'var(--bg-elevated)',
                color: categoryFilter === '' ? 'white' : 'var(--text-secondary)',
                border: categoryFilter === '' ? 'none' : '1px solid var(--border)',
              }}
            >
              All {typeFilter === 'asset' ? 'Tools / Assets' : 'Bulk / Consumables'}
            </button>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {sortedCategories.map((c, i) => {
                const count    = Number(c.item_count) || 0;
                const selected = categoryFilter === c.code;
                const [light, dark] = ROW_COLORS[Math.floor(i / 2) % ROW_COLORS.length];
                const bg = i % 2 === 0 ? light : dark;
                return (
                  <button
                    key={c.category_id || c.code}
                    onClick={() => setCategoryFilter(selected ? '' : c.code)}
                    style={{
                      minHeight: 48, padding: '8px 6px', borderRadius: 10,
                      cursor: 'pointer', textAlign: 'center', background: bg,
                      color: 'white',
                      // Selection is a bright ring rather than a colour change,
                      // since every tile already owns a colour.
                      border: selected ? '2px solid #FFFFFF' : '2px solid transparent',
                      // No dimming for empty categories — it muddied the row
                      // colours, and the item count already says "0 items".
                    }}
                  >
                    <span style={{
                      display: 'block', fontSize: '0.8rem', fontWeight: 700,
                      lineHeight: 1.15,
                      // Hard guarantee of a single line: if a new category is
                      // added without a short label it clips instead of wrapping.
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {shortLabel(c)}
                    </span>
                    <span style={{
                      display: 'block', fontWeight: 400, fontSize: '0.68rem',
                      opacity: 0.85, marginTop: 2,
                    }}>
                      {count} item{count !== 1 ? 's' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="loading">
            <div className="spinner" />
          </div>
        )}

        {/* Empty state */}
        {!loading && results.length === 0 && (
          <div className="empty-state">
            <div style={{ fontSize: '2.5rem' }}>🔍</div>
            <p>No items found</p>
            <p style={{ fontSize: '0.85rem' }}>
              {query || typeFilter || statusFilter || categoryFilter
                ? 'Try adjusting your search or filters'
                : 'No inventory has been added yet'}
            </p>
          </div>
        )}

        {/* Results */}
        {!loading && results.length > 0 && (
          <div className="card">
            {results.map(item => (
              <div
                key={item.item_id}
                className="item-row"
                onClick={() => navigate(`/item/${item.item_id}`)}
              >
                <div className="item-row-icon" style={{ padding: 0, overflow: 'hidden' }}>
                  {item.photo_url
                    ? <img src={thumbUrl(item.photo_url)} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                    : getCategoryIcon(item.category_code)
                  }
                </div>
                <div className="item-row-info">
                  <h4>{item.name}</h4>
                  <p style={{ fontSize: '0.8rem' }}>
                    {item.asset_tag || item.sku || ''}
                    {item.asset_tag && item.category_name ? ' · ' : ''}
                    {item.category_name}
                  </p>
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {item.current_location || 'Unknown location'}
                    {item.assigned_to ? ` · ${item.assigned_to}` : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  {getStatusBadge(item.current_status)}
                  {item.has_pending_transfer && (
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 700, borderRadius: 4,
                      padding: '2px 5px', background: '#2563eb', color: 'white',
                      whiteSpace: 'nowrap'
                    }}>
                      Transfer Pending
                    </span>
                  )}
                  {item.has_pending_request && (
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 700, borderRadius: 4,
                      padding: '2px 5px', background: '#d97706', color: 'white',
                      whiteSpace: 'nowrap'
                    }}>
                      Requested
                    </span>
                  )}
                  {item.item_type === 'bulk' && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      {item.available_quantity} {item.unit_of_measure}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}